import { useState, createContext, useContext, useEffect } from "react";
import i18next, { exists } from "i18next";
import { ITDContext, ConsoleLog, ModalManager, ModalProps, useSingleQuery, ModalState } from "@inntechcorp/it-design";
import Cookies from "js-cookie";
import moment from "moment";
import { useLocation } from "react-router";
import { toast } from "sonner";

//import Socket from "./SocketContext";
import { ErrorDetailProps, usePostFetch } from "Api/hooks/usePostFetch";
import { GetLocalState, SetLocalState } from "Connectivity/LocalStorage";
import ModalData from "Helpers/modal/ModalData";
import { MakeLoginData } from "Helpers/make/MakeLoginData";
import { WebContextProps } from "Types/WebContextProps";
import { PageTemplateProps, PrivateSocketBalanceProps } from "Types/index";
import { DeviceTypes, PlatformTypes, ThemeTypes } from "Types/enums";
import { EmptyModalStruct } from "Helpers/struct/EmptyModalStruct";
import useUserHubActions from "Actions/useUserHubActions";

import { StyledProvider } from "./StyledContext";
import { GlobalStyle } from "../GlobalStyle";
import { ThemeProvider } from "./ThemeContext";
import PrivateSocketContext from "./PrivateSocketContext";

import { ThemeColor } from "../types/ThemeContextProps";
import { LoginDataProps } from "../types/LoginDataProps";
import { DefaultLoginStruct } from "../helpers/struct/DefaultLoginStruct";
import { LoginResultProps } from "../types/LoginResultProps";
import { DateFormatProps } from "../types/DateFormatProps";

import 'moment/dist/locale/tr';
import PublicSocketContext from "./PublicSocketContext";
import { DefaultActivePageStruct } from "Helpers/struct/DefaultActivePageStruct";
import { PagePopupProps } from "Types/PageProps";

const WebContext = createContext<WebContextProps>({} as WebContextProps);
      WebContext.displayName = "WebContext";
      
export const WebProvider = ({ children }:{children:any}) => {

  const { 
    changeLanguage,
    changeLocale, 
    changeDateFormat, 
    changeTheme 
  } = useContext(ITDContext);
  
  const {
    listenPrivateSocket,
    cancelPrivateSocket,
    balance,
    gameSession,
    status: privateSocketStatus
  } = useContext(PrivateSocketContext);

  const {
    listenPublicSocket,
    cancelPublicSocket,
    jackpot,
    subscribeToGame,
    status: publicSocketStatus
  } = useContext(PublicSocketContext);

  const { removeUserHubParams }                       = useUserHubActions();
  const { fetch }                                     = usePostFetch();
  const { search, pathname }                          = useLocation();
  const SQ                                            = useSingleQuery();
  const {
    showModal,
    removeAllModals,
    modalEvent
  }= ModalManager.useModal();

  const [initialized,               SetInitialized]               = useState(false);
  const [login,                     SetLogin]                     = useState<LoginDataProps>(DefaultLoginStruct);
  const [isPrivate,                 SetIsPrivate]                 = useState(false);
  const [isLoggedIn,                SetLoggedIn]                  = useState(false);
  const [isLoggingOut,              SetLoggingOut]                = useState(false);
  const [isOnline,                  SetIsOnline]                  = useState(false);
  const [isMobile,                  SetIsMobile]                  = useState(false);
  const [isGameOpen,                SetIsGameOpen]                = useState(false);
  const [isEmailVerificationOpen,   SetIsEmailVerificationOpen]   = useState(false);
  const [isResetPasswordOpen,       SetIsResetPasswordOpen]       = useState(false);
  const [isUserHubOpen,             SetIsUserHubOpen]             = useState(false);
  const [isModalOpen,               SetIsModalOpen]               = useState(false);
  const [isMegaMenuOpen,            SetIsMegaMenuOpen]            = useState(false);
  const [isUserEnterOpen,           SetIsUserEnterOpen]           = useState(false);
  const [isLiveSupportOpen,         SetIsLiveSupportOpen]         = useState(false);
  const [deviceType,                SetDeviceType]                = useState(DeviceTypes.DESKTOP);
  const [hasBodyLock,               SetHasBodyLock]               = useState(false);
  const [hideAmount,                SetHideAmount]                = useState(false);
  const [activePage,                SetActivePage]                = useState<PageTemplateProps>(DefaultActivePageStruct);
  const [theme,                     SetTheme]                     = useState<ThemeColor>(ThemeTypes.DARK);
  const [flag,                      SetFlag]                      = useState("");
  const [language,                  SetLanguage]                  = useState("");
  const [currency,                  SetCurrency]                  = useState("");
  const [timezone,                  SetTimeZone]                  = useState("");
  const [remainingPopups,           SetRemainingPopups]           = useState<PagePopupProps[]>([]);
  const [activePopup,               SetActivePopup]               = useState("");
  const [userBalance,               SetUserBalance]               = useState<PrivateSocketBalanceProps>({
    activeBalance     : "0.00",
    casinoBonus       : "0.00",
    free              : "0.00",
    reservedWithdraw  : "0.00",
    sportBonus        : "0.00",
    totalBalance      : "0.00",
    totalBonus        : "0.00",
    fetched           : false
  });
  const [dateFormat,    SetDateFormat]    = useState<DateFormatProps>({
    Moment    : "",
    Date      : "",
    Time      : "",
    FullDate  : "",
    HumanDate : "",
    DateTime  : ""
  });

  const { t } = i18next;
  const settings = GetLocalState("settings");

  const hasLiveSupportOpen = () => {
    return SQ.get(settings.queries.LiveSupport, "") !== "";
  }

  const hasMegaMenuOpen = () => {
    return SQ.get(settings.queries.MegaMenu, "") !== "";
  }

  const hasUserEnterOpen = () => {
    return SQ.get(settings.queries.UserEnter, "") !== "";
  }

  const hasGameOpen = () => {
    return SQ.get(settings.queries.Game, "") !== "";
  }
  
  const hasUserHubOpen = () => {
    return SQ.get(settings.queries.UserHub, "") === settings.values.Open;
  }

  const hasEmailVerificationOpen = () => {
    return pathname.includes(settings.paths.EmailVerificationPage);
  }

  const hasResetPasswordOpen = () => {
    return pathname.includes(settings.paths.ResetPasswordPage);
  }
  
  const addNotificationError = (message = "", description = "") => {
    toast.error(
      exists(message) ? t(message) : "",
      {
        duration      : 3000,
        description   : exists(description) ? t(description) : "",
      }
    );
  };

  const getLanguageDataBySlug = (slug:any) => { 
    return GetLocalState("languages").filter((l:any) => l.Slug === slug)[0];
  }
  
  /* ---------------------------------------- */

  useEffect(() => {
    
    SetUserBalance(balance);

  }, [balance]);

  useEffect(() => {
    
    if ( !initialized ) return;
    
    checkQueryStringsAndParams();

  }, [search, pathname]);

  useEffect(() => {

    let isOpen;

    if ( modalEvent.event === ModalState.NOT_SHOW) return;

    if ( modalEvent.event === ModalState.BEGIN ){
      isOpen = true;
    }
    else if (
      modalEvent.event === ModalState.EXITING
      ||
      modalEvent.event === ModalState.ALL_REMOVED
    ){
      isOpen = false;
    }
    
    if ( isOpen !== undefined ) {
      SetIsModalOpen(isOpen);
    }

  }, [modalEvent]);

  const checkQueryStringsAndParams = () => {

    const _isLiveSupportOpen        = hasLiveSupportOpen();
    const _isMegaMenuOpen           = hasMegaMenuOpen();
    const _isUserEnterOpen          = hasUserEnterOpen();
    const _isGameOpen               = hasGameOpen();
    const _isUserHubOpen            = hasUserHubOpen();
    const _isEmailVerificationOpen  = hasEmailVerificationOpen();
    const _isResetPasswordOpen      = hasResetPasswordOpen();

    if ( _isEmailVerificationOpen !== isEmailVerificationOpen ) {
      SetIsEmailVerificationOpen(_isEmailVerificationOpen);
    }

    if ( _isResetPasswordOpen !== isResetPasswordOpen ) {
      SetIsResetPasswordOpen(_isResetPasswordOpen);
    }

    if ( _isGameOpen !== isGameOpen ) {
      SetIsGameOpen(_isGameOpen);
    }
    
    if ( _isUserHubOpen !== isUserHubOpen ) {
      SetIsUserHubOpen(_isUserHubOpen);
    }
    
    if ( _isLiveSupportOpen !== isLiveSupportOpen ) {
      SetIsLiveSupportOpen(_isLiveSupportOpen);
    }
    
    if ( _isMegaMenuOpen !== isMegaMenuOpen ) {
      SetIsMegaMenuOpen(_isMegaMenuOpen);
    }
    
    if ( _isUserEnterOpen !== isUserEnterOpen ) {
      SetIsUserEnterOpen(_isUserEnterOpen);
    }

    if ( _isGameOpen !== hasBodyLock ) {
      ChangeBodyLock(_isGameOpen);
    }

    listenPublicSocket(
      GetLocalState('settings').socket.Path,
      GetLocalState('settings').socket.Namespace,
      GetLocalState('settings').socket.PublicRoom
    );

  }

  const ChangeLogin = async (
    data        : LoginResultProps | null = null,
    parseLogin  : boolean = true,
    isRegister  : boolean = false
  ) => {

    if ( !data ) {
      ConsoleLog.single("🔴 Login data can't provided!", "error");
      return;
    }
    
    const loginData = parseLogin ? MakeLoginData(data) : data;

    SetLoggedIn(true);
    SetLogin(loginData as LoginDataProps);
    SetLocalState("login", loginData);

    listenPrivateSocket(
      GetLocalState('settings').socket.Path,
      GetLocalState('settings').socket.Namespace,
      GetLocalState('settings').socket.PrivateRoom,
      (loginData as LoginDataProps).tokenHash
    );

    const notificationTitle   = isRegister ? "signUp" : "signIn";
    const notificationSuccess = isRegister ? "signUpSuccess" : "signInSuccess";

    if ( parseLogin ) {

      const description = `${notificationTitle}.notifications.${notificationSuccess}`;

      setTimeout(() => {  
        toast.success(
          t(`${notificationTitle}.notifications.${notificationTitle}Title`),
          {
            duration      : 3000,
            description   : exists(description) ? t(description) : "",
          }
        );
      }, 1000);
    }
    
  };

  const ChangeLogout = (status = "") => {

    ConsoleLog.single("🚨 ChangeLogout > Status: " + status);

    SetLoggingOut(true);
    removeAllModals();
    removeUserHubParams(true);

    if ( status === "tokenIsExpired" || status === "userNotLoggedIn" ) {
      RemoveLoginTokens();
      return;
    }

    if ( 
      status === "badGateway" 
      || status === "error" 
      || status === "serviceTemporarilyUnavailable" 
      || status === "internalServerError" 
      || status === "noResponseFromApiPath" 
      || status === "unhandledError" 
    ) {
      RemoveLoginTokens();
      addNotificationError(`notifications.${status}`);
      return;
    }

    fetch(
      "post",
      `/api/user-fe/logout`,
      {},
      { 
        onFetched: () => {

          const title = "signIn.notifications.logoutTitle";
          const description = "signIn.notifications.logoutSuccess";

          RemoveLoginTokens();

          toast.success(
            exists(title) ? t(title) : "",
            {
              duration      :  2000,
              description   : exists(description) ? t(description) : "",
            }
          );

        },
        onError: (err?:ErrorDetailProps):void => {

          const title       = "signIn.notifications.logoutTitle";
          const description = "signIn.notifications.logoutFailed";

          ConsoleLog.single("🔴 ChangeLogout > Error: " + err?.detail, "error");

          toast.error(
            exists(title) ? t(title) : "",
            {
              duration      : 2000,
              description   : exists(description) ? t(description) : "",
            }
          );
          RemoveLoginTokens();
          //addNotificationError("notifications.logoutFailed")
        }
      }
    );

  };

  /**
   * 
   * Ön tanımlı modal açmak için kullanılır. Config'ten gelen
   * modal değerlerine göre açar ve içeriğini belirler. Ön tanımlı
   * olmayan modal açmak için ITD kütüphanesindeki ModalManager 
   * kullanılması gereklidir.
   * 
   * @param {*} name Açılacak modal'ın adı
   * 
   */
  const ShowModal = (id:string, name:string, props?:Partial<ModalProps>) => {

    let modalProps:ModalProps = {
      ...EmptyModalStruct,
      ...props,
      id                    : id,
      okButtonText          : t("buttons.OK"),
      cancelButtonText      : t("buttons.cancel"),
      customProps           : props?.customProps ? props.customProps : {}
    }

    showModal(ModalData(name, modalProps, getContextValue));

  };

  const RemoveLoginTokens = async () => {
    cancelPrivateSocket();
    SetLocalState("login", {});
    Cookies.remove("web-token");
    SetLogin(DefaultLoginStruct);
    SetLoggedIn(false);
    SetLoggingOut(false);
  }

  const ChangePrivate = (value:boolean) => {
    Cookies.set("web-private", value.toString());
    SetIsPrivate(value);
  };

  const ChangeTheme = (value:ThemeColor) => {
    Cookies.set("web-theme", value);
    SetTheme(value);
    changeTheme(value);
  };
  
  const ChangeFlag = (value:string) => {
    Cookies.set("web-flag", value);
    SetFlag(value);
  }
  
  const ChangeLanguage = (value:string) => {

    moment.locale(value);
    i18next.changeLanguage(value);
    Cookies.set("web-language", value);
    
    SetLanguage(value);

    changeLocale(value);
    changeLanguage(value);

    const format = getLanguageDataBySlug(value).Format;
    ChangeDateFormat(format);

  };

  const ChangeCurrency = (value:string) => {
    Cookies.set("web-currency", value);
    SetCurrency(value);
  };

  const ChangeTimezone = (value:string) => {
    Cookies.set("web-timezone", value);
    SetTimeZone(value);
  };

  const ChangeDateFormat = (format:DateFormatProps) => {
    SetDateFormat(format);
    changeDateFormat(format.Moment);
    Cookies.set("web-date", JSON.stringify(format));
  };

  const ChangeIsOnline = (value:boolean) => {
    
    if ( value ) {
      ConsoleLog.single("📡 Connection restored. Internet connection active.",);
    }
    else {
      ConsoleLog.single("🕯️ Connection error. There is no Internet connection.", "error");
    }
    
    SetIsOnline(value);

    if ( !value ) ShowModal("disconnect-modal", "disconnect");
    
  };

  const ChangeIsMobile = (value:boolean) => {
    Cookies.set("web-platform", value ? PlatformTypes.MOBILE : PlatformTypes.DESKTOP);
    SetIsMobile(value);
  };

  const ChangeDeviceType = (value:DeviceTypes) => {
    SetDeviceType(value);
  };

  const ChangeBodyLock = (value:boolean) => {
    SetHasBodyLock(value);
  };

  const ChangeHideAmount = (value:boolean) => {
    SetHideAmount(value);
  };

  const ChangeActivePage = (value:PageTemplateProps) => {
    SetActivePage(value);
  };

  const ChangeActivePopup = (value:string) => {
    SetActivePopup(value);
  };

  const ChangeInitialized = (value:boolean) => {

    SetInitialized(value);
    if ( value ) checkQueryStringsAndParams();

  };

  const AddPopupsToQueue = (popups: PagePopupProps[]) => {
    SetRemainingPopups(popups);
  };

  const ClearPopupQueue = () => {
    SetRemainingPopups([]);
  };

  /* ---------------------------------------- */

  /*
  function wait(ms) {
    return new Promise( (resolve) => {setTimeout(resolve, ms)});
  }
  */

  /* ---------------------------------------- */
  
  const getContextValue:WebContextProps = {
    ShowModal,
    ChangeLogout,
    RemoveLoginTokens,
    ChangeInitialized,
    AddPopupsToQueue,
    ClearPopupQueue,
    initialized,
    login,            ChangeLogin,
    theme,            ChangeTheme,
    flag,             ChangeFlag,
    language,         ChangeLanguage,
    currency,         ChangeCurrency,
    timezone,         ChangeTimezone,
    dateFormat,       ChangeDateFormat,
    isMobile,         ChangeIsMobile,
    isOnline,         ChangeIsOnline,
    deviceType,       ChangeDeviceType,
    hasBodyLock,      ChangeBodyLock,
    isPrivate,        ChangePrivate,
    hideAmount,       ChangeHideAmount,
    activePage,       ChangeActivePage,
    activePopup,      ChangeActivePopup,
    userBalance,
    isLoggedIn,
    isLoggingOut,
    isGameOpen,
    isEmailVerificationOpen,
    isResetPasswordOpen,
    isUserHubOpen,
    isLiveSupportOpen,
    isMegaMenuOpen,
    isUserEnterOpen,
    isModalOpen,
    remainingPopups,
    publicSocket: {
      jackpot,
      status: publicSocketStatus,
      subscribeToGame,
      listenPublicSocket,
      cancelPublicSocket
    },
    privateSocket: {
      balance,
      status: privateSocketStatus,
      gameSession,
      listenPrivateSocket,
      cancelPrivateSocket
    }
  };

  return (
    <WebContext.Provider value={getContextValue}>
      <StyledProvider theme={theme} mobile={isMobile}>
        <ThemeProvider>
          <GlobalStyle />
          { children }
        </ThemeProvider>
      </StyledProvider>
    </WebContext.Provider>
  )

};

export default WebContext;

